import { registerSiteContent } from '@/features/siteContent/runtime';

/**
 * The copy around the plans on `/pricing`. Editable by the platform owner
 * under Site content.
 *
 * Plans, prices, limits and ticks are not here - they come from the plan rows
 * at `/platform/plans`. Many lines below are filled in from those rows at
 * render time; a `{placeholder}` is replaced with the value its comment names,
 * so an edit should keep the placeholders it needs.
 */

export const PRICING_PAGE = {
  hero: {
    bannerAlt: 'Everything included with every NeonCarts plan',
    headline: "You've got big plans.",
    headlineAccent: 'So do we.',
    /**
     * The line under the headline, which depends on what is sold. `{which}` is
     * `whichEvery` or `whichSome`; `{trial}` is one of the `trial` phrases.
     */
    subtitle: {
      freeAndTrial:
        'Start free, no credit card. {which} begins with {trial} - nothing is charged until it ends.',
      freeOnly: 'Start free, no credit card. Upgrade only when your store is earning it.',
      trialOnly: '{which} begins with {trial}. Nothing is charged until it ends.',
      neither: 'Transparent pricing with no lock-in. Change or cancel your plan at any time.',
      whichEvery: 'Every paid plan',
      whichSome: 'Paid plans marked with a trial',
    },
  },
  /** How the trial is described in a sentence: `{days}`, or `{min}` and `{max}` days. */
  trial: {
    single: 'a {days}-day free trial',
    range: 'a free trial of {min} to {max} days depending on the plan',
  },
  /** A plan's button. `{days}` is its trial length, `{plan}` its name. */
  planCta: {
    free: 'Start for free',
    trial: 'Start {days}-day trial',
    choose: 'Choose {plan}',
  },
  popularBadge: 'MOST POPULAR',
  /** A sales-led plan (Enterprise): no price, a conversation instead. */
  contactSales: {
    price: 'Call Sales',
    note: 'Custom pricing for your business',
    cta: 'Talk to sales',
    to: '/talk-to-sales',
  },
  price: {
    /** Under a monthly figure: `{days}` is the trial length. */
    trialFootnote: 'Starts after a {days}-day free trial',
    /** Added after "Billed yearly" and the like: `{days}` is the trial length. */
    billedTrialSuffix: ' after a {days}-day trial',
    /** `{fee}` is the setup fee, `{products}` and `{images}` what it covers. */
    setup:
      'Optional store setup by our team: {fee} one-time, up to {products} products with {images} images each.',
    /** After the unit when the platform adds GST on top of its prices. */
    plusGst: '+ GST',
    /** Under the cards when GST applies: `{rate}` is the percentage. */
    gstNote: 'Prices exclude GST. {rate}% GST is added at checkout, and you get a GST invoice.',
  },
  included: {
    heading: 'Every plan ships with the essentials',
    /**
     * Each tile is only shown while every plan on sale grants the feature it
     * promises; which feature that is stays in code.
     */
    tiles: {
      checkout: {
        title: 'Checkout that converts',
        description: 'Mobile-first cart and checkout, on every plan.',
      },
      design: {
        title: 'Polished store design',
        description: 'Professional themes and a no-code editor, from day one.',
      },
      ai: {
        title: 'AI assistant built in',
        description: 'Product copy, SEO and campaign drafts included, with clear limits.',
      },
      support: {
        title: 'Real support',
        description: 'Help from people who understand Indian commerce.',
      },
    },
  },
  comparison: {
    heading: 'Compare all features',
    subheading: 'Every row is what the platform enforces - no asterisks.',
    toggleLabel: 'Show only the features the plans differ on',
    toggleText: 'Only show what differs',
    featuresColumn: 'Features',
    allSame: 'Every plan includes exactly the same things.',
    included: 'Included',
    notIncluded: 'Not included',
    addon: 'Add-on',
  },
  faq: {
    heading: 'Pricing questions, answered',
    /** Joins the last two names in a list: "Growth and Pro". */
    listAnd: ' and ',
    /** `{trial}` is one of the `trial` phrases, `{plan}` the free plan's name. */
    trial: {
      question: 'How does the free trial work?',
      answer:
        'Paid plans start with {trial}. You set up your payment method when you sign up, but nothing is charged until the trial ends - the first payment is taken on the day it finishes, and the plan renews automatically after that. Cancel from your billing page before the trial ends and you pay nothing at all.',
      answerWithFreePlan:
        'Paid plans start with {trial}. You set up your payment method when you sign up, but nothing is charged until the trial ends - the first payment is taken on the day it finishes, and the plan renews automatically after that. Cancel from your billing page before the trial ends and you pay nothing at all - your store simply moves to the {plan} plan.',
    },
    /** Asked instead of the trial question when there is no trial; `{plan}` is the free plan. */
    freePlan: {
      question: 'Can I start without paying?',
      answer:
        'Yes. The {plan} plan costs nothing and needs no credit card. Build your store, take real orders on it, and move up only when the limits start to pinch.',
    },
    changePlan: {
      question: 'Can I change or cancel my plan anytime?',
      answer:
        'Yes. Upgrades apply immediately and downgrades apply from your next billing cycle. There are no lock-ins and no cancellation fees - your data stays yours either way.',
    },
    /** Shown while every plan has zero commission. */
    fees: {
      question: 'Are there any transaction fees?',
      answer:
        'NeonCarts does not add its own commission on your sales, on any plan. Standard payment gateway charges from Razorpay apply to online payments, as they would anywhere.',
    },
    /** `{features}` lists the monthly limits; the answer is their own descriptions. */
    metered: {
      question: 'How are {features} counted?',
    },
    /** Shown while every plan has GST invoices; `{plan}` is the free plan. */
    gst: {
      question: 'Do I get GST-compliant invoices on every plan?',
      answer:
        'Yes. Every order on every plan generates a GST-compliant invoice with the correct CGST/SGST/IGST treatment and sequential numbering.',
      answerWithFreePlan:
        'Yes. Every order on every plan - including {plan} - generates a GST-compliant invoice with the correct CGST/SGST/IGST treatment and sequential numbering.',
    },
    /**
     * `{subdomain}` is `subdomain` while every plan has a free subdomain,
     * `{plans}` the plans with a custom domain, `{everyPlan}` is `everyPlan`
     * when that is all of them.
     */
    domain: {
      question: 'Can I use my own domain?',
      answer:
        '{subdomain}Connecting your own .in or .com domain is available on {plans}{everyPlan}.',
      subdomain: 'Every store gets a free NeonCarts subdomain with SSL. ',
      everyPlan: ' - every plan',
    },
  },
  closing: {
    heading: 'Turn plans into progress.',
    subheading: 'Launch in minutes and upgrade only when your store is earning it.',
    to: '/register',
    /** The button, which depends on what there is to start. */
    ctaFree: 'Start for free',
    ctaTrial: 'Start free trial',
    ctaDefault: 'Create your store',
  },
  loading: 'Loading plans',
  /** When the plans cannot be loaded; split around the sign-up link. */
  loadError: {
    before: 'We could not load our plans just now. Refresh in a moment, or',
    linkLabel: 'start your store',
    to: '/register',
    after: '- you can choose a plan from inside it at any time.',
  },
};

registerSiteContent('page.pricing', { PRICING_PAGE });
